import { useEffect, useMemo, useState } from "react";

import { Editor, Extension } from "@tiptap/react";
import {
  ArrowUp,
  ChevronDown,
  LayoutTemplate,
  Mic,
  Redo2,
  Square,
  Undo2,
} from "lucide-react";

import { useScenarioTemplates } from "@/components/helpers/custom-hooks/use-scenario-templates";
import {
  getString,
  removeItem,
  setString,
} from "@/components/helpers/storage/local-storage-utils";
import {
  convertHtmlToMarkdown,
  convertMarkdownToHtml,
} from "@/components/helpers/string-utils";
import { Flex } from "@/components/layout/flexbox";
import { useAuth } from "@/components/lib/auth";
import { ScenarioContexts } from "@/components/pages_logged_in/roleplays/details/create/ScenarioContexts";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ExpandableTextInput } from "@/components/ui/expandable-text-input";
import { WorkspaceContextFragment } from "@/graphql";
import { cn } from "@/lib/utils";

interface CreateScenarioInputProps {
  value: string;
  onChange: (value: string) => void;
  onCreate: (props: {
    content: string;
    selectedTemplate?: { id: string; name: string; content: string };
    attachedContexts?: string[];
  }) => void;
  onVoiceChat: () => void;
  loading: boolean;
  disableTemplateSelect?: boolean;
  onStopScenarioCreation?: () => void;
  containerCardClassName?: string;
  disableVoiceSetup?: boolean;
  placeholder?: string;
  sessionId?: string | null;
}

export function CreateScenarioInput({
  value,
  onChange,
  onCreate,
  onVoiceChat,
  loading,
  disableTemplateSelect = false,
  onStopScenarioCreation,
  containerCardClassName,
  disableVoiceSetup,
  placeholder,
  sessionId,
}: CreateScenarioInputProps) {
  const [attachedContexts, setAttachedContexts] = useState<
    WorkspaceContextFragment[]
  >([]);
  const { currentWorkspace } = useAuth();

  const disableWorkspaceContextSelect = currentWorkspace.isPersonal;
  const { templatesByCategory, templatesById } = useScenarioTemplates();
  const [editor, setEditor] = useState<Editor | null>(null);
  const selectedTemplate = templatesById[editor?.$doc.attributes.templateId];
  const [canUndo, setCanUndo] = useState(false);
  const [canClear, setCanClear] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [isEditorFocused, setIsEditorFocused] = useState(false);
  const [submitState, setSubmitState] = useState<{
    isSubmit: boolean;
    content: string | undefined | null;
  }>({
    isSubmit: false,
    content: undefined,
  });

  const onSubmit = (editorContent: string) => {
    if (!loading) {
      const content = editorContent || value;

      onCreate({
        content,
        selectedTemplate,
        attachedContexts: attachedContexts.map((context) => context.id),
      });

      editor?.commands.clearContent();
      setCanClear(false);

      setSubmitState({
        isSubmit: false,
        content: undefined,
      });
    }
  };

  const onEditorContentUpdate = (content: string) => {
    if (!editor) {
      return;
    }

    const editorCurrentCanUndo = editor.can().undo();
    const editorCurrentCanClear = convertHtmlToMarkdown(content) !== "";
    const editorCurrentCanRedo = editor.can().redo();

    if (editorCurrentCanUndo !== canUndo) {
      setCanUndo(editorCurrentCanUndo);
    }
    if (editorCurrentCanClear !== canClear) {
      setCanClear(editorCurrentCanClear);
    }
    if (editorCurrentCanRedo !== canRedo) {
      setCanRedo(editorCurrentCanRedo);
    }

    onChange(content);
  };

  useEffect(() => {
    if (submitState.isSubmit && submitState.content) {
      onSubmit(submitState.content);
    }
  }, [submitState]);

  useEffect(() => {
    if (!loading) {
      setSubmitState({ isSubmit: false, content: undefined });
    }
  }, [loading]);

  useEffect(() => {
    setSubmitState({ isSubmit: false, content: undefined });
  }, [sessionId]);

  useEffect(() => {
    if (!editor) return;

    const handleFocus = () => setIsEditorFocused(true);
    const handleBlur = () => setIsEditorFocused(false);

    editor.on("focus", handleFocus);
    editor.on("blur", handleBlur);

    return () => {
      editor.off("focus", handleFocus);
      editor.off("blur", handleBlur);
    };
  }, [editor]);

  const KeyboardHandler = Extension.create({
    name: "keyboardHandler",
    addKeyboardShortcuts() {
      return {
        "Mod-Enter": () => {
          if (!this.editor.isEmpty) {
            setSubmitState((prev) => {
              if (!prev.isSubmit) {
                return {
                  isSubmit: true,
                  content: this.editor.getHTML(),
                };
              }

              return prev;
            });
          }

          return true;
        },

        "Shift-Enter": () => {
          /**
           * currently we do not have an option to show a soft line break in the posts, so we overwrite
           * the behavior from tiptap with the default behavior on pressing enter
           */
          return this.editor.commands.first(({ commands }) => [
            () => commands.newlineInCode(),
            () => commands.createParagraphNear(),
            () => commands.liftEmptyBlock(),
            () => commands.splitBlock(),
          ]);
        },
      };
    },
  });

  const ScenarioContextsMemo = useMemo(
    () => <ScenarioContexts onChange={setAttachedContexts} />,
    [setAttachedContexts],
  );

  return (
    <Card
      className={cn(
        "min-h-0 w-full max-w-4xl flex-auto gap-4 p-4 border-1 transition-colors",
        isEditorFocused ? "border-blue-400" : "border-gray-300",
        containerCardClassName,
      )}
      onClick={(e) => {
        if (!(e.target as HTMLElement)?.closest("button")) {
          editor?.commands.focus();
        }
      }}
    >
      <CardHeader className="flex flex-wrap justify-between p-0">
        <Flex className="flex-wrap gap-2">
          {!disableTemplateSelect ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button intent="secondary" variant="outline" size="sm">
                  <LayoutTemplate />
                  <span className="sm:block hidden">
                    {selectedTemplate ? selectedTemplate.name : "Use Template"}
                  </span>
                  <ChevronDown />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                style={{ minWidth: "var(--radix-dropdown-menu-trigger-width)" }}
              >
                {Object.entries(templatesByCategory).map(
                  ([category, templates]) => (
                    <DropdownMenuSub key={category}>
                      <DropdownMenuSubTrigger>
                        {category}
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent>
                        {templates.map((template) => (
                          <DropdownMenuItem
                            key={template.id}
                            onClick={() => {
                              const html = convertMarkdownToHtml(
                                template.content,
                              );
                              onChange(html);
                              setString(
                                "scenarioPromptTemplateId",
                                template.id,
                              );
                              editor
                                ?.chain()
                                .setTemplateId(template.id)
                                .setContent(html)
                                .run();

                              setCanClear(true);
                            }}
                          >
                            {template.name}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  ),
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
          {ScenarioContextsMemo}
        </Flex>
        {!disableVoiceSetup && (
          <Button variant="outline" size="sm" onClick={onVoiceChat}>
            <Mic />
            <span className="sm:block hidden">Voice Setup</span>
          </Button>
        )}
      </CardHeader>
      {!(
        disableVoiceSetup &&
        disableTemplateSelect &&
        disableWorkspaceContextSelect
      ) && <hr className="m-0 mb-4" />}
      <CardContent
        className="-mt-4 flex flex-auto flex-col overflow-y-auto p-0 min-h-[90px]"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            editor?.commands.focus();
          }
        }}
      >
        <div>
          <ExpandableTextInput
            onEditorChange={setEditor}
            value={value}
            onChange={onEditorContentUpdate}
            placeholder={placeholder}
            templateId={getString("scenarioPromptTemplateId")}
            extensions={[KeyboardHandler]}
          />
        </div>
      </CardContent>
      <CardFooter className="flex flex-wrap justify-between gap-2 p-0">
        <div className="flex gap-2">
          <Button
            intent="secondary"
            variant="outline"
            size="sm"
            disabled={!canClear}
            onClick={() => {
              removeItem("scenarioPrompt");
              onEditorContentUpdate("");
              editor?.chain().setTemplateId("").setContent("").run();
              removeItem("scenarioPromptTemplateId");
            }}
          >
            Clear
          </Button>
          <Button
            intent="secondary"
            variant="outline"
            size="sm"
            onClick={() => {
              editor?.chain().focus().undo().run();
              const templateId = editor?.state.doc.attrs.templateId;
              if (templateId) {
                setString("scenarioPromptTemplateId", templateId);
              } else {
                removeItem("scenarioPromptTemplateId");
              }
            }}
            disabled={!canUndo}
          >
            <Undo2 />
          </Button>
          <Button
            intent="secondary"
            variant="outline"
            size="sm"
            onClick={() => {
              editor?.chain().focus().redo().run();
              const templateId = editor?.state.doc.attrs.templateId;
              if (templateId) {
                setString("scenarioPromptTemplateId", templateId);
              } else {
                removeItem("scenarioPromptTemplateId");
              }
            }}
            disabled={!canRedo}
          >
            <Redo2 />
          </Button>
        </div>
        <div className="flex items-center gap-2 [scrollbar-gutter:stable]">
          <Button
            intent="primary"
            size="sm"
            onClick={() => {
              const content = editor?.getHTML() || value;

              onCreate({
                content,
                selectedTemplate,
                attachedContexts: attachedContexts.map((context) => context.id),
              });

              editor?.commands.setContent("");
              onEditorContentUpdate("");
            }}
            disabled={loading}
          >
            {loading ? (
              "Working..."
            ) : (
              <>
                Send
                <ArrowUp color="white" className="w-4 h-4" />
              </>
            )}
          </Button>
          {loading && (
            <Button intent="primary" size="sm" onClick={onStopScenarioCreation}>
              <Square className="w-2 h-2 bg-white" color="white" />
            </Button>
          )}
        </div>
      </CardFooter>
    </Card>
  );
}
